import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Plus, Trash2 } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

const PRESETS = [
  { label: "Diário 08:00", cron: "0 8 * * *" },
  { label: "Segunda 09:00", cron: "0 9 * * 1" },
  { label: "Sexta 17:00", cron: "0 17 * * 5" },
  { label: "Hora em hora", cron: "0 * * * *" },
];

export default function ScheduledSkillsManager() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["scheduled_tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scheduled_tasks")
        .select("*")
        .order("next_run_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const toggle = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("scheduled_tasks").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduled_tasks"] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("scheduled_tasks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled_tasks"] });
      toast({ title: "Tarefa removida" });
    },
  });

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Admin" }, { label: "Scheduled Skills" }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-headline flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" /> Scheduled Skills
          </h1>
          <p className="text-sm text-muted-foreground">Skills disparadas por agendamento (cron). Plano v17.07 §III.4.</p>
        </div>
        <CreateTaskDialog open={open} onOpenChange={setOpen} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{tasks.length} tarefas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma tarefa agendada.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Skill</TableHead>
                  <TableHead>Cron</TableHead>
                  <TableHead>TZ</TableHead>
                  <TableHead>Próxima</TableHead>
                  <TableHead>Última</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{t.name}</TableCell>
                    <TableCell><Badge variant="outline">{t.skill_name}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{t.cron_expression}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{t.timezone}</TableCell>
                    <TableCell className="text-xs">{t.next_run_at ? new Date(t.next_run_at).toLocaleString("pt-BR") : "—"}</TableCell>
                    <TableCell className="text-xs">{t.last_run_at ? new Date(t.last_run_at).toLocaleString("pt-BR") : "—"}</TableCell>
                    <TableCell>
                      <Switch checked={t.active} onCheckedChange={(v) => toggle.mutate({ id: t.id, active: v })} />
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => remove.mutate(t.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CreateTaskDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [skillName, setSkillName] = useState("daily-summary");
  const [cron, setCron] = useState("0 8 * * *");
  const [tz, setTz] = useState("America/Sao_Paulo");

  const create = useMutation({
    mutationFn: async () => {
      const { data: org } = await supabase.from("org_members").select("org_id").limit(1).maybeSingle();
      if (!org?.org_id) throw new Error("Sem organização");
      const { error } = await supabase.from("scheduled_tasks").insert([{
        org_id: org.org_id,
        name: name || `${skillName} ${cron}`,
        skill_name: skillName,
        cron_expression: cron,
        timezone: tz,
        active: true,
        payload: {},
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled_tasks"] });
      toast({ title: "Tarefa criada" });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button><Plus className="w-4 h-4 mr-1" /> Nova tarefa</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Agendar skill</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome (opcional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Resumo diário 08h" />
          </div>
          <div>
            <Label>Skill</Label>
            <Input value={skillName} onChange={(e) => setSkillName(e.target.value)} placeholder="daily-summary" />
          </div>
          <div>
            <Label>Cron</Label>
            <div className="flex gap-2">
              <Input value={cron} onChange={(e) => setCron(e.target.value)} className="font-mono" />
              <Select onValueChange={setCron}>
                <SelectTrigger className="w-44"><SelectValue placeholder="Preset" /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => <SelectItem key={p.cron} value={p.cron}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Timezone</Label>
            <Input value={tz} onChange={(e) => setTz(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} disabled={create.isPending}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
